import VideoShield from './video-shield';
import TextShield from './text-shield';
import ImageShield from './image-shield';

interface WebShieldOptions {
  video?: boolean | VideoOptions;
  text?: boolean | TextOptions;
  image?: boolean | ImageOptions;
  observeDynamicContent?: boolean; // Global option that applies to all shields
}

interface VideoOptions {
  preventRightClick?: boolean;
  hideControls?: boolean;
  disableDevTools?: boolean;
  targetSelector?: string;
  observeDynamicContent?: boolean;
}

interface TextOptions {
  preventSelection?: boolean;
  preventCopy?: boolean;
  preventPrintScreen?: boolean;
  targetSelector?: string;
  observeDynamicContent?: boolean;
}

interface ImageOptions {
  preventRightClick?: boolean;
  preventDrag?: boolean;
  addWatermark?: boolean | string;
  targetSelector?: string;
  observeDynamicContent?: boolean;
}

class WebShield {
  private static readonly VERSION = '0.1.1'; // Match the new version
  private options: WebShieldOptions;
  private videoShield: VideoShield | null = null;
  private textShield: TextShield | null = null;
  private imageShield: ImageShield | null = null;

  constructor(options: WebShieldOptions = {}) {
    this.options = options;
    this.initialize();
  }

  private initialize(): void {
    try {
      if (this.options.video) {
        const videoOptions = typeof this.options.video === 'boolean' ? {} : this.options.video;
        // Apply global observeDynamicContent setting if not explicitly set in video options
        if (this.options.observeDynamicContent !== undefined && videoOptions.observeDynamicContent === undefined) {
          videoOptions.observeDynamicContent = this.options.observeDynamicContent;
        }
        this.videoShield = new VideoShield(videoOptions);
      }
      
      if (this.options.text) {
        const textOptions = typeof this.options.text === 'boolean' ? {} : this.options.text;
        // Apply global observeDynamicContent setting if not explicitly set in text options
        if (this.options.observeDynamicContent !== undefined && textOptions.observeDynamicContent === undefined) {
          textOptions.observeDynamicContent = this.options.observeDynamicContent;
        }
        this.textShield = new TextShield(textOptions);
      }
      
      if (this.options.image) {
        const imageOptions = typeof this.options.image === 'boolean' ? {} : this.options.image;
        // Apply global observeDynamicContent setting if not explicitly set in image options
        if (this.options.observeDynamicContent !== undefined && imageOptions.observeDynamicContent === undefined) {
          imageOptions.observeDynamicContent = this.options.observeDynamicContent;
        }
        this.imageShield = new ImageShield(imageOptions);
      }
    } catch (error) {
      console.error('web-shield: Error initializing WebShield', error);
    }
  }

  private verifyIntegrity(): void {
    // Check if code has been tampered with
    try {
      if (WebShield.VERSION !== '0.1.1') { // Match version manually
        console.warn('web-shield: Library integrity may be compromised');
      }
    } catch (error) {
      // Silent fail to avoid breaking functionality
    }
  }

  disable(): void {
    try {
      this.videoShield?.disable();
      this.textShield?.disable();
      this.imageShield?.disable();
    } catch (error) {
      console.error('web-shield: Error disabling WebShield', error);
    }
  }
}

// Export WebShield as the default export
export default WebShield;

// Export individual shield classes for more flexible usage
export { VideoShield, TextShield, ImageShield };

// Export interfaces for better TypeScript support
export type { WebShieldOptions, VideoOptions, TextOptions, ImageOptions };